// Hooks de React Query para o domínio CRM Deal (Story 11.4).
// - useCrmDealsInfinite — listagem paginada por cursor (DR-032) + filtros.
// - useCrmDeal — detalhe.
// - useCreateCrmDeal / useUpdateCrmDeal / useChangeCrmDealStage.
//
// Convenção: queryKey ['crm-deals', 'list', filters] e ['crm-deals', 'detail', id].
// Após qualquer mutation: invalidateQueries({ queryKey: ['crm-deals'] }).

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import {
  changeCrmDealStage,
  createCrmDeal,
  fetchCrmDeal,
  fetchCrmDeals,
  fetchCrmDealsKanban,
  updateCrmDeal,
  type CrmDealDetailEntity,
  type CrmDealEntity,
  type CrmDealKanban,
  type CrmDealKanbanParams,
  type CrmDealListParams,
  type CrmDealListResponse,
} from '../lib/crm-deals-api';
import type {
  ChangeCrmDealStageDto,
  CreateCrmDealDto,
  CrmDealStageSchema,
  UpdateCrmDealDto,
} from '@control-center/shared';

const DEFAULT_LIMIT = 20;

export const crmDealsKeys = {
  all: ['crm-deals'] as const,
  lists: () => [...crmDealsKeys.all, 'list'] as const,
  list: (params: Omit<CrmDealListParams, 'cursor'>) => [...crmDealsKeys.lists(), params] as const,
  kanbans: () => [...crmDealsKeys.all, 'kanban'] as const,
  kanban: (params: CrmDealKanbanParams) => [...crmDealsKeys.kanbans(), params] as const,
  details: () => [...crmDealsKeys.all, 'detail'] as const,
  detail: (id: string) => [...crmDealsKeys.details(), id] as const,
};

export type CrmDealsListFilters = Omit<CrmDealListParams, 'cursor' | 'limit'>;
export type CrmDealsKanbanFilters = CrmDealKanbanParams;

export function useCrmDealsInfinite(filters: CrmDealsListFilters = {}) {
  return useInfiniteQuery<
    CrmDealListResponse,
    Error,
    InfiniteData<CrmDealListResponse>,
    ReturnType<typeof crmDealsKeys.list>,
    string | undefined
  >({
    queryKey: crmDealsKeys.list({ ...filters, limit: DEFAULT_LIMIT }),
    queryFn: ({ pageParam, signal }) =>
      fetchCrmDeals({ ...filters, limit: DEFAULT_LIMIT, cursor: pageParam }, signal),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
    staleTime: 30_000,
  });
}

export function useCrmDeal(id: string | undefined) {
  return useQuery<CrmDealDetailEntity, Error>({
    queryKey: id ? crmDealsKeys.detail(id) : ['crm-deals', 'detail', 'noop'],
    queryFn: ({ signal }) => fetchCrmDeal(id as string, signal),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateCrmDeal() {
  const qc = useQueryClient();
  return useMutation<CrmDealEntity, Error, CreateCrmDealDto>({
    mutationFn: (payload) => createCrmDeal(payload),
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: crmDealsKeys.lists() });
      qc.invalidateQueries({ queryKey: crmDealsKeys.detail(deal.id) });
    },
  });
}

export interface UpdateCrmDealArgs {
  id: string;
  payload: UpdateCrmDealDto;
}

export function useUpdateCrmDeal() {
  const qc = useQueryClient();
  return useMutation<CrmDealEntity, Error, UpdateCrmDealArgs>({
    mutationFn: ({ id, payload }) => updateCrmDeal(id, payload),
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: crmDealsKeys.lists() });
      qc.invalidateQueries({ queryKey: crmDealsKeys.detail(deal.id) });
    },
  });
}

export interface ChangeCrmDealStageArgs {
  id: string;
  payload: ChangeCrmDealStageDto;
}

export function useChangeCrmDealStage() {
  const qc = useQueryClient();
  return useMutation<CrmDealEntity, Error, ChangeCrmDealStageArgs>({
    mutationFn: ({ id, payload }) => changeCrmDealStage(id, payload),
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: crmDealsKeys.lists() });
      qc.invalidateQueries({ queryKey: crmDealsKeys.detail(deal.id) });
    },
  });
}

// =============================================================================
// Kanban (Story 11.5) — query agrupada + move otimista com rollback.
// =============================================================================

/** GET /api/crm/deals?view=kanban — deals open agrupados por estágio (AC2). */
export function useCrmDealsKanban(filters: CrmDealsKanbanFilters = {}) {
  return useQuery<CrmDealKanban, Error>({
    queryKey: crmDealsKeys.kanban(filters),
    queryFn: ({ signal }) => fetchCrmDealsKanban(filters, signal),
    staleTime: 15_000,
  });
}

export interface MoveCrmDealStageArgs {
  deal: CrmDealEntity;
  toStage: CrmDealStageSchema;
  /** Motivo obrigatório ao mover para fechamento_perdido (AC6). */
  reason?: string;
}

interface MoveStageContext {
  /** Snapshot do cache pra rollback (AC5) — chave + valor anterior. */
  previous: [readonly unknown[], CrmDealKanban | undefined][];
}

/**
 * Move um deal de estágio com atualização otimista (AC5/FR-51.5).
 * - `onMutate`: move o card no cache imediatamente (sem aguardar a API) e guarda
 *   um snapshot pra rollback. Multi-coluna safe (remove da origem, adiciona no destino).
 * - `onError`: restaura o snapshot — card volta à coluna original.
 * - `onSettled`: invalida o kanban (sincroniza version/stageEnteredAt do servidor).
 *
 * Filtros do kanban ativo são necessários pra mirar a queryKey correta no
 * snapshot/rollback. Não bloqueia drags concorrentes (queue permitida — AC9).
 */
export function useMoveCrmDealStage(filters: CrmDealsKanbanFilters = {}) {
  const qc = useQueryClient();
  return useMutation<CrmDealEntity, Error, MoveCrmDealStageArgs, MoveStageContext>({
    mutationFn: ({ deal, toStage, reason }) =>
      changeCrmDealStage(deal.id, {
        stage: toStage,
        version: deal.version,
        ...(reason ? { reason } : {}),
      } as ChangeCrmDealStageDto),

    onMutate: async ({ deal, toStage }) => {
      const key = crmDealsKeys.kanban(filters);
      await qc.cancelQueries({ queryKey: key });

      const previous: MoveStageContext['previous'] = [[key, qc.getQueryData<CrmDealKanban>(key)]];

      qc.setQueryData<CrmDealKanban>(key, (current) =>
        current ? moveDealInKanban(current, deal, toStage) : current,
      );

      return { previous };
    },

    onError: (_err, _vars, ctx) => {
      ctx?.previous.forEach(([key, snapshot]) => {
        qc.setQueryData(key, snapshot);
      });
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: crmDealsKeys.kanbans() });
      qc.invalidateQueries({ queryKey: crmDealsKeys.lists() });
    },
  });
}

/**
 * Move um deal entre colunas no mapa kanban (imutável — novo objeto).
 * Remove o deal de qualquer coluna onde estiver e o insere no topo do destino
 * com `stage` atualizado. Idempotente se o deal já estiver no destino.
 */
function moveDealInKanban(
  kanban: CrmDealKanban,
  deal: CrmDealEntity,
  toStage: CrmDealStageSchema,
): CrmDealKanban {
  const next = {} as CrmDealKanban;
  for (const stage of Object.keys(kanban) as CrmDealStageSchema[]) {
    next[stage] = kanban[stage].filter((d) => d.id !== deal.id);
  }
  next[toStage] = [{ ...deal, stage: toStage }, ...next[toStage]];
  return next;
}
